import {
  addDoc,
  collection,
  serverTimestamp,
  FirestoreDataConverter,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  UserRequirement,
  FunctionalSpecification,
  TestCase,
  RiskRecord,
  StandardOperatingProcedure,
} from '../types';

// Firestore converters keep our domain models typed on reads/writes.
const passthrough = <T>() =>
  ({
    toFirestore: (data: T) => data,
    fromFirestore: (snap) => snap.data() as T,
  }) as FirestoreDataConverter<T>;

const requirementConverter = passthrough<UserRequirement>();
const fsConverter = passthrough<FunctionalSpecification>();
const testConverter = passthrough<TestCase>();
const riskConverter = passthrough<RiskRecord>();
const sopConverter = passthrough<StandardOperatingProcedure>();

export async function createRequirement(projectId: string, data: UserRequirement) {
  const col = collection(db, 'projects', projectId, 'urs').withConverter(requirementConverter);
  const now = serverTimestamp();
  // Store custom ID in data, but Firestore generates its own document ID
  const { id: customId, ...dataWithoutId } = data;
  const docRef = await addDoc(col, {
    ...dataWithoutId,
    id: customId, // Store custom ID (URS-001, etc.) in document data
    createdAt: now,
    updatedAt: now,
  } as any);
  return { ...data, id: customId, _firestoreId: docRef.id };
}

export async function createFunctionalSpec(projectId: string, data: FunctionalSpecification) {
  const col = collection(db, 'projects', projectId, 'fs').withConverter(fsConverter);
  const now = serverTimestamp();
  const { id: customId, ...dataWithoutId } = data;
  const docRef = await addDoc(col, {
    ...dataWithoutId,
    id: customId,
    createdAt: now,
    updatedAt: now,
  } as any);
  return { ...data, id: customId, _firestoreId: docRef.id };
}

export async function createTestCase(projectId: string, data: TestCase) {
  const col = collection(db, 'projects', projectId, 'tests').withConverter(testConverter);
  const now = serverTimestamp();
  const { id: customId, ...dataWithoutId } = data;
  const docRef = await addDoc(col, {
    ...dataWithoutId,
    id: customId,
    createdAt: now,
    updatedAt: now,
  } as any);
  return { ...data, id: customId, _firestoreId: docRef.id };
}

export async function createRisk(projectId: string, data: RiskRecord) {
  const col = collection(db, 'projects', projectId, 'risks').withConverter(riskConverter);
  const now = serverTimestamp();
  const { id: customId, ...dataWithoutId } = data;
  const docRef = await addDoc(col, {
    ...dataWithoutId,
    id: customId,
    createdAt: now,
    updatedAt: now,
  } as any);
  return { ...data, id: customId, _firestoreId: docRef.id };
}

export async function createSOP(projectId: string, data: StandardOperatingProcedure) {
  const col = collection(db, 'projects', projectId, 'sops').withConverter(sopConverter);
  const now = serverTimestamp();
  const { id: customId, ...dataWithoutId } = data;
  const docRef = await addDoc(col, {
    ...dataWithoutId,
    id: customId,
    createdAt: now,
    updatedAt: now,
  } as any);
  return { ...data, id: customId, _firestoreId: docRef.id };
}

